import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// HIPAA: Session timeout configuration (15 minutes of inactivity)
const SESSION_TIMEOUT_MINUTES = 15;
const SESSION_WARNING_MINUTES = 2; // Warn 2 minutes before timeout

let lastActivityTime = Date.now();
let sessionTimeoutId = null;
let sessionWarningId = null;

/**
 * Extract error message from API error response.
 * Handles Pydantic validation errors (422) which return an array of error objects.
 * 
 * @param {Error} error - The axios error object
 * @param {string} fallbackMessage - Default message if no detail found
 * @returns {string} - Human-readable error message
 */
export const getErrorMessage = (error, fallbackMessage = 'An error occurred') => {
  const detail = error.response?.data?.detail;
  
  if (typeof detail === 'string') {
    return detail;
  } else if (Array.isArray(detail) && detail.length > 0) {
    // Pydantic validation errors return array of {loc, msg, type} objects
    const firstError = detail[0];
    const field = firstError.loc?.slice(-1)[0]; // Get the field name
    const message = firstError.msg || firstError.message || 'Validation error';
    return field ? `${field}: ${message}` : message;
  } else if (detail?.msg) {
    return detail.msg;
  } else if (error.message) {
    return error.message;
  }
  
  return fallbackMessage;
};

// Update last activity time on user interaction
const updateActivity = () => {
  lastActivityTime = Date.now();
};

// Initialize activity listeners
if (typeof window !== 'undefined') {
  ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'].forEach(event => {
    document.addEventListener(event, updateActivity, { passive: true });
  });
}

// Check for session timeout
const checkSessionTimeout = () => {
  const token = localStorage.getItem('token');
  if (!token) return;
  
  const inactiveTime = Date.now() - lastActivityTime;
  const timeoutMs = SESSION_TIMEOUT_MINUTES * 60 * 1000;
  const warningMs = (SESSION_TIMEOUT_MINUTES - SESSION_WARNING_MINUTES) * 60 * 1000;
  
  if (inactiveTime >= timeoutMs) {
    // Session expired due to inactivity
    handleSessionTimeout();
  } else if (inactiveTime >= warningMs) {
    // Show warning (if not already shown)
    const remainingSeconds = Math.ceil((timeoutMs - inactiveTime) / 1000);
    if (window.sessionWarningCallback) {
      window.sessionWarningCallback(remainingSeconds);
    }
  }
};

// Handle session timeout
const handleSessionTimeout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('hospital');
  
  // Clear any existing timeouts
  if (sessionTimeoutId) clearInterval(sessionTimeoutId);
  if (sessionWarningId) clearTimeout(sessionWarningId);
  
  // Redirect to login with message
  window.location.href = '/login?reason=timeout';
};

// Start session monitoring
const startSessionMonitoring = () => {
  if (sessionTimeoutId) clearInterval(sessionTimeoutId);
  lastActivityTime = Date.now();
  sessionTimeoutId = setInterval(checkSessionTimeout, 30000); // Check every 30 seconds
};

// Stop session monitoring
const stopSessionMonitoring = () => {
  if (sessionTimeoutId) {
    clearInterval(sessionTimeoutId);
    sessionTimeoutId = null;
  }
};

// Export session management functions
export const sessionManager = {
  start: startSessionMonitoring,
  stop: stopSessionMonitoring,
  updateActivity,
  getLastActivity: () => lastActivityTime,
  getRemainingTime: () => {
    const inactiveTime = Date.now() - lastActivityTime;
    const timeoutMs = SESSION_TIMEOUT_MINUTES * 60 * 1000;
    return Math.max(0, Math.ceil((timeoutMs - inactiveTime) / 1000));
  },
  setWarningCallback: (callback) => {
    window.sessionWarningCallback = callback;
  }
};

const api = axios.create({
  baseURL: API_BASE,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    // Update activity on API calls
    updateActivity();
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('hospital');
      stopSessionMonitoring();
      window.location.href = '/login?reason=expired';
    } else if (error.response?.status === 423) {
      // Account locked
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login?reason=locked';
    } else if (error.response?.status === 429) {
      // Rate limited - don't redirect, let the component handle it
      console.warn('Rate limit exceeded');
    }
    return Promise.reject(error);
  }
);

export default api;